import { Link, NavLink, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { canUse, firstAdminPath, hasBackOffice, sectionFor, visibleSections } from './access';

/** True when the signed-in account may use the admin screen at the current address. */
export function useAdminAccess() {
  const { auth, user } = useAuth();
  const { pathname } = useLocation();
  return Boolean(auth?.accessToken && canUse(user, sectionFor(pathname)));
}

export function AdminNav() {
  const { user } = useAuth();
  return (
    <nav className="admin-nav" aria-label="Admin sections">
      {visibleSections(user).map(({ path, label }) => (
        <NavLink key={path} to={path} className={({ isActive }) => (isActive ? 'active' : '')}>{label}</NavLink>
      ))}
    </nav>
  );
}

/** /admin: opens the first screen the account can use. */
export function AdminHome() {
  const { auth, user } = useAuth();
  if (auth?.accessToken && hasBackOffice(user)) return <Navigate to={firstAdminPath(user)} replace />;
  return <AdminShell title="Admin" />;
}

/** Admin page frame: sign-in and access check, section tabs and the page heading. */
export default function AdminShell({ title, subtitle, actions, children }) {
  const { auth, user } = useAuth();
  const hasAccess = useAdminAccess();
  const { pathname, search } = useLocation();

  if (!auth?.accessToken) {
    return (
      <section className="container admin-page">
        <h1>Admin access required</h1>
        <p>Please sign in with an administrator or staff account.</p>
        <Link className="btn btn-primary" to={`/account/login?returnTo=${encodeURIComponent(pathname + search)}`}>Sign in</Link>
      </section>
    );
  }
  if (!hasAccess) {
    return (
      <section className="container admin-page">
        {hasBackOffice(user) && <AdminNav />}
        <h1>No access to this screen</h1>
        <p>{hasBackOffice(user)
          ? 'Your staff account has not been given this screen. Ask an administrator if you need it.'
          : 'Your account does not have administrator or staff access.'}</p>
      </section>
    );
  }

  return (
    <section className="container admin-page">
      <AdminNav />
      <div className="admin-head">
        <div><h1>{title}</h1>{subtitle && <p className="muted">{subtitle}</p>}</div>
        {actions && <div className="admin-actions">{actions}</div>}
      </div>
      {children}
    </section>
  );
}
